import firebase from "firebase/compat/app";
import { default as configuredFirebase } from "../../firebase/firebase";
import { useFirestore } from "react-redux-firebase";
import { v4 as uuidv4 } from "uuid";
import { DateTime } from "luxon";

// Locals
import { useUserId } from "../../authentication/hooks";
import useCustomSnackbar from "../../components/snackbar";
import {
  firebaseTimestampOrNull,
  nonEmptyStringOrNull,
  enforceObject,
} from "../../helpers/format";

// Types
import type { SingleItemState, StorageItem } from "../types";

interface EditItem {
  (
    wsId: string,
    itemId: string,
    updatedItem: Partial<SingleItemState> & { [key: string]: unknown },
    deactivatedUser?: boolean,
  ): Promise<void>;
}

interface UseEditItem {
  (simple?: boolean): EditItem;
}

export const useEditItem: UseEditItem = (simple?: boolean) => {
  const firestore = useFirestore();
  const loggedInUserId = useUserId();
  const { enqueueSnackbar } = useCustomSnackbar();

  return async (
    wsId: string,
    itemId: string,
    updatedItem: Partial<SingleItemState> & { [key: string]: unknown },
    deactivatedUser?: boolean,
  ) => {
    const cleanUpdatedItem: Partial<SingleItemState> = simple
      ? {
          ...updatedItem,
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
          updatedBy: loggedInUserId,
        }
      : {
          ...updatedItem,
          children: enforceObject(updatedItem.children),
          parent: nonEmptyStringOrNull(updatedItem.parent),
          user: nonEmptyStringOrNull(updatedItem.user),
          bought: firebaseTimestampOrNull(
            updatedItem.bought as firebase.firestore.Timestamp | DateTime | null,
          ),
          warrantyUntil: firebaseTimestampOrNull(
            updatedItem.warrantyUntil as firebase.firestore.Timestamp | DateTime | null,
          ),
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
          updatedBy: loggedInUserId,
        };

    const ws = firestore.collection("workspaces").doc(wsId);
    const oldItem = ws.collection("items").doc(itemId);

    try {
      await oldItem.update(cleanUpdatedItem);
      enqueueSnackbar("Item info was updated.", "success");
    } catch (err: unknown) {
      if (deactivatedUser)
        enqueueSnackbar("The user of this item is deactivated, please change him.", "error");
      else {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        enqueueSnackbar(`Failed to update item info. ${err?.message}`, "error");
      }
      console.error(err);
    }
  };
};

interface AddItem {
  (wsId: string, newItem: SingleItemState, files?: File[]): Promise<{ id: string } | null>;
}

interface UseAddItem {
  (): AddItem;
}

export const useAddItem: UseAddItem = () => {
  const { enqueueSnackbar } = useCustomSnackbar();
  const attachFiles = useAttachFilesToItem();

  return async (wsId: string, item: SingleItemState, files?: File[]) => {
    const newItem = {
      ...item,
      children: enforceObject(item.children),
      parent: nonEmptyStringOrNull(item.parent),
      user: nonEmptyStringOrNull(item.user),
      bought: firebaseTimestampOrNull(
        item.bought as firebase.firestore.Timestamp | DateTime | null,
      ),
      warrantyUntil: firebaseTimestampOrNull(
        item.warrantyUntil as firebase.firestore.Timestamp | DateTime | null,
      ),
    };

    try {
      const createItemFunction = configuredFirebase.functions().httpsCallable("createItem");
      const result = await createItemFunction({
        workspaceId: wsId,
        item: newItem,
      });

      const itemId = result.data.id;

      if (files && files.length) {
        attachFiles(files, wsId, itemId);
      }

      enqueueSnackbar("Item was saved.", "success");

      return { id: itemId };
    } catch (error) {
      const errorCode = (error as { code?: string })?.code;
      const errorMessage = (error as { message?: string })?.message;

      if (errorCode === "functions/resource-exhausted") {
        enqueueSnackbar(errorMessage || "Item limit exceeded", "error");
      } else if (errorCode === "functions/permission-denied") {
        enqueueSnackbar("You don't have permission to create items in this workspace.", "error");
      } else if (errorCode === "functions/invalid-argument") {
        enqueueSnackbar(
          `Invalid item data: ${errorMessage || "Unknown validation error"}`,
          "error",
        );
      } else {
        enqueueSnackbar(`Failed to save new item. ${errorMessage || "Unknown error"}`, "error");
      }

      return null;
    }
  };
};

interface RemoveItem {
  (wsId: string, itemId: string): Promise<void>;
}

interface UseRemoveItem {
  (): RemoveItem;
}

export const useRemoveItem: UseRemoveItem = () => {
  const firestore = useFirestore();
  const loggedInUserId = useUserId();
  const { enqueueSnackbar } = useCustomSnackbar();

  return async (wsId: string, itemId: string) => {
    if (!loggedInUserId) {
      enqueueSnackbar(`Failed to find your id.`, "error");
      console.error(`Failed to find your id.`);
      return;
    }

    try {
      await firestore.doc(`workspaces/${wsId}/items/${itemId}`).delete();
      enqueueSnackbar("Item was deleted.", "success");
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      enqueueSnackbar(`Failed to delete item. ${err?.message}`, "error");
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      console.error(err.message);
    }
  };
};

interface AttachFiles {
  (files: File[], wsId: string, itemId: string): Promise<void>;
}

interface UseAttachFiles {
  (): AttachFiles;
}

export const useAttachFilesToItem: UseAttachFiles = () => {
  const { enqueueSnackbar } = useCustomSnackbar();
  const actorId = firebase.auth().currentUser?.uid ?? "";

  return async (files: File[], wsId: string, itemId: string) => {
    const wsItemStorageRef = firebase.storage().ref(`workspaces/${wsId}/items/${itemId}/files`);

    await Promise.all(
      files.map(async (file) => {
        try {
          const uniqueName = uuidv4();
          const exactRef = wsItemStorageRef.child(`/${uniqueName}`);
          await exactRef.put(file, {
            customMetadata: {
              name: file.name,
              createdAt: DateTime.utc().toString(),
              createdBy: actorId,
            },
          });
        } catch (err) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          enqueueSnackbar(`Failed to attach file ${file.name}. ${err?.message}`, "error");
          console.error(err);
        }
      }),
    );
  };
};

interface GetFiles {
  (path: string): Promise<StorageItem[] | undefined>;
}

interface UseGetFilesFromPath {
  (): GetFiles;
}

export const useGetFilesFromPath: UseGetFilesFromPath = () => {
  const { enqueueSnackbar } = useCustomSnackbar();

  return async (path: string) => {
    try {
      const storageRef = firebase.storage().ref(path);
      const response = await storageRef.listAll();

      return await Promise.all(
        response.items.map(async (itemRef) => {
          const meta = await itemRef.getMetadata();

          return {
            // For displaying correct name
            name: meta.customMetadata?.name || meta.name || "Unknown name",
            // For generating links
            dbName: itemRef.name,
          };
        }),
      );
    } catch (err) {
      enqueueSnackbar(
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        `Something went wrong with accessing attachments. ${err?.message}`,
        "error",
      );
      console.error(err);
      return;
    }
  };
};

interface GetFileUrl {
  (path: string): Promise<string>;
}

interface UseGetFileUrl {
  (): GetFileUrl;
}

export const useGetFileUrl: UseGetFileUrl = () => {
  const { enqueueSnackbar } = useCustomSnackbar();

  return async (path: string) => {
    try {
      const response = await configuredFirebase.functions().httpsCallable("getSignedUrlOfFile")({
        path,
      });

      return response?.data?.signedUrl;
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      enqueueSnackbar(`Failed to open file. ${err?.message}`, "error");
      console.error(err);
    }
  };
};

interface DeleteFile {
  (workspaceId: string, itemId: string, fileName: string): Promise<void>;
}

interface UseDeleteFile {
  (): DeleteFile;
}

export const useDeleteFile: UseDeleteFile = () => {
  const { enqueueSnackbar } = useCustomSnackbar();

  return async (workspaceId: string, itemId: string, fileName: string) => {
    try {
      await configuredFirebase.functions().httpsCallable("archiveFile")({
        workspaceId,
        itemId,
        fileName,
      });
      enqueueSnackbar("File successfuly deleted.", "success");
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      enqueueSnackbar(`Failed to delete file. ${err?.message}`, "error");
      console.error(err);
    }
  };
};
